import { useQuery } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

export interface Asset {
  id: string;
  url: string;
  thumbnailUrl: string;
  originalFilename: string;
  aspectRatio: string;
  width: number;
  height: number;
}

export interface PaginationLinks {
  self: string;
  first: string;
  last: string;
  prev: string | null;
  next: string | null;
}

interface GetAssetsResponse {
  assets: Asset[];
  pagination: {
    total: number;
    currentPage: number;
    links: PaginationLinks;
  };
}

interface UseGetAssetsParams {
  organisationId: string;
  aspectRatio?: string;
  page?: number;
  enabled?: boolean;
}

function getAssets({
  organisationId,
  aspectRatio,
  page,
}: {
  organisationId: string;
  aspectRatio?: string;
  page?: number;
}): Promise<GetAssetsResponse> {
  return axiosPrivate
    .get<GetAssetsResponse>('/v3/pharmacist/assets', {
      params: {
        organisationId,
        limit: 9,
        ...(aspectRatio ? { aspectRatio } : {}),
        ...(page ? { page } : {}),
      },
    })
    .then((r) => r.data);
}

export function useGetAssets({
  organisationId,
  aspectRatio,
  page = 1,
  enabled = true,
}: UseGetAssetsParams) {
  const {
    data,
    isInitialLoading: isLoading,
    isError,
  } = useQuery({
    queryKey: ['pharmacist-assets', organisationId, aspectRatio, page],
    queryFn: () => getAssets({ organisationId, aspectRatio, page }),
    enabled: !!organisationId && enabled,
    meta: { errorMessage: 'Failed to load assets' },
  });

  return {
    assets: data?.assets ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    isError,
  };
}
